import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ILeafletInstance } from '../../lib/leaflet/leaflet.interface';
import { IMapWidgetFormGroupSettings } from '../../map-widget-settings/map-widget-settings.interface';
import { IMapWidgetPermissions } from '../../../shared/interfaces/map-widget-permission.interface';
import { DomSanitizer } from '@angular/platform-browser';
import {
  maritimeTargetsTLLAIS,
  maritimeTargetsTTD,
  radarTargetSvg
} from '../../lib/leaflet/markers/div-icons/div-icons-constant';
import { IDataTypeSpec } from '../../lib/ngsild/helpers/leaflet-instance-config.constants';


export interface IMapToolbarSettingDisplayChanges {
  AIS: {
    aISTargetsVisible: boolean;
    action: string
  };
  MaritimeTargets: {
    maritimeTargetsVisible: boolean;
    action: string
  },
  FusionMaritimeTarget: {
    fusionMaritimeTargetVisible: boolean;
    action: string;
  }
  RadarTarget: {
    radarTargetVisible: boolean;
    action: string
  }
  global: {
    showPermanentTooltip: boolean;
  }
}

@Component({
    selector: 'lib-map-toolbar-settings',
    imports: [SharedModule],
    templateUrl: './map-toolbar-settings.component.html',
    styleUrl: './map-toolbar-settings.component.scss'
})
export class MapToolbarSettingsComponent implements AfterViewInit {
  sanitizer=  inject(DomSanitizer);
  @ViewChild('slideMenuContainer', { static: false }) slideMenuContainer!: ElementRef;
  slideMenuContent!: HTMLElement;

  @Output() toolbarSettingsChanged = new EventEmitter<any>();

  widgetId: string | undefined;
  leafletInstance: ILeafletInstance | undefined;
  widgetSettings: Partial<IMapWidgetFormGroupSettings> | undefined;
  widgetPermissions: IMapWidgetPermissions | undefined;

  initialMapSettings = {
    aisTargetsActive: false,
    maritimeTargetsActive: false,
    fusionMaritimeTargetActive: false,
    radarTargetActive: false,
  }

  settingDisplayChanges: IMapToolbarSettingDisplayChanges = {
    AIS: {
      aISTargetsVisible: this.initialMapSettings.aisTargetsActive,
      action: 'enableAisTargets',
    },
    MaritimeTargets: {
      maritimeTargetsVisible: this.initialMapSettings.maritimeTargetsActive,
      action: 'enableMaritimeTargets'
    },
    FusionMaritimeTarget: {
      fusionMaritimeTargetVisible: this.initialMapSettings.fusionMaritimeTargetActive,
      action: 'enableFusionMaritimeTarget'
    },
    RadarTarget: {
      radarTargetVisible: this.initialMapSettings.radarTargetActive,
      action: 'enablePlannedPath'
    },
    global: {
      showPermanentTooltip: true
    }
  }

  protected readonly radarTargetSvg = radarTargetSvg;
  protected readonly maritimeTargetsTLLAIS = maritimeTargetsTLLAIS;
  protected readonly maritimeTargetsTTD = maritimeTargetsTTD;

  ngAfterViewInit(): void {
    this.initialMapSettings.aisTargetsActive =
        this.widgetSettings?.enableAisTargets || false
    this.initialMapSettings.maritimeTargetsActive =
        this.widgetSettings?.enableMaritimeTargets || false;
    this.initialMapSettings.fusionMaritimeTargetActive =
        this.widgetSettings?.enableRadarTarget || false;
    this.initialMapSettings.radarTargetActive =
        this.widgetSettings?.enableFusionMaritimeTarget || false;

    this.settingDisplayChanges.AIS.aISTargetsVisible = this.initialMapSettings.aisTargetsActive;
    this.settingDisplayChanges.MaritimeTargets.maritimeTargetsVisible = this.initialMapSettings.maritimeTargetsActive;
    this.settingDisplayChanges.RadarTarget.radarTargetVisible = this.initialMapSettings.fusionMaritimeTargetActive;
    this.settingDisplayChanges.FusionMaritimeTarget.fusionMaritimeTargetVisible = this.initialMapSettings.radarTargetActive;
  }

  closeSlideMenu(): void {
   const slideMenuControl = this.leafletInstance?.layerControls.get(`slideMenuControl_${this.widgetId}`) as any;

    if (slideMenuControl) {
      // Call the internal library `_animate` method to close the menu
      const menu = slideMenuControl._menu;
      const isHorizontal = slideMenuControl._isHorizontal;

      const menuSize = isHorizontal ?
          parseInt(slideMenuControl.options.width, 10) :
          parseInt(slideMenuControl.options.height, 10);
      const from = 0;
      const to = -menuSize;

      slideMenuControl._animate(menu, from, to, false, true, menuSize * 0.1);
    }
  }

  toggleSwitcherEvent($event: MatSlideToggleChange, eventAction: string): void {
    const isChecked = $event.checked;

    const actionsMap: Record<string, () => void> = {
      enableAisTargets: () => this.toggleLayerVisibility('AIS', isChecked),
      enableMaritimeTargets: () => this.toggleLayerVisibility('MaritimeTargets', isChecked),
      enablePlannedPath: () => this.toggleLayerVisibility('RadarTarget', isChecked),
      enableFusionMaritimeTarget: () => this.toggleLayerVisibility('FusionMaritimeTarget', isChecked),
      mapShowPermanentTooltip: () => this.toggleToolTipTitle(isChecked)
    };

    actionsMap[eventAction]?.();
  }

  private toggleLayerVisibility(layerKey: keyof IMapToolbarSettingDisplayChanges, displayLayer: boolean): void {
    const leafletInstance = this.leafletInstance;
    if (!leafletInstance) return;

    const dataModel = (leafletInstance.dataConfig.dataModel as Record<string, IDataTypeSpec[keyof IDataTypeSpec]>)[layerKey];
    if (!dataModel) return;

    let layerId = `${leafletInstance.id}_${dataModel.type}`;
    if (dataModel.tempLayerSuffix) {
      layerId += dataModel.tempLayerSuffix;
    }

    const layer = leafletInstance.layers.get(layerId);

    if (leafletInstance.map) {
      displayLayer ? layer?.addTo(leafletInstance.map) : layer?.removeFrom(leafletInstance.map);
    }

    const visibilityKey = dataModel.tempLayerSuffix ?
        `${this.lowercaseFirstLetter(layerKey)}TargetsVisible` :
        `${this.lowercaseFirstLetter(layerKey)}Visible`;

    if (this.settingDisplayChanges[layerKey] && visibilityKey in this.settingDisplayChanges[layerKey]) {
      (this.settingDisplayChanges[layerKey] as any)[visibilityKey] = displayLayer;
    }

    this.toolbarSettingsChanged.emit(this.settingDisplayChanges);
  }

  private toggleToolTipTitle(isChecked: boolean): void {
    this.settingDisplayChanges.global.showPermanentTooltip = isChecked;
    this.toolbarSettingsChanged.emit(this.settingDisplayChanges);
  }

  private lowercaseFirstLetter(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }
}
